import { useState, useEffect } from 'react';
import { products, categories, priceTiers } from '@/mocks/products';
import ProductCard from './ProductCard';

interface ProductListProps {
  activeTier?: string;
  onTierChange?: (tier: string) => void;
}

function getCategoryFromHash(): string {
  const hash = window.location.hash;
  const match = hash.match(/[?&]category=([^&]*)/);
  return match ? match[1] : 'all';
}

export default function ProductList({ activeTier = 'all', onTierChange }: ProductListProps) {
  const [activeCategory, setActiveCategory] = useState<string>(getCategoryFromHash);

  useEffect(() => {
    const handleHashChange = () => {
      setActiveCategory(getCategoryFromHash());
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const filtered = products.filter((p) => {
    const matchCategory = activeCategory === 'all' || p.categoryId === activeCategory;
    const matchTier = activeTier === 'all' || p.tier === activeTier;
    return matchCategory && matchTier;
  });

  const currentTierInfo = priceTiers.find((t) => t.id === activeTier);

  return (
    <section id="products" className="py-20 bg-[#FAF8F5]">
      <div className="px-6 lg:px-16">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#8B4513] mb-3 font-medium">
            嚴選優質肉品
          </p>
          <h3 className="text-4xl lg:text-5xl font-black text-[#1a1a1a] tracking-tight">
            {activeTier === 'all' ? '精選商品' : currentTierInfo?.label ?? '精選商品'}
          </h3>
          {activeTier !== 'all' && currentTierInfo && (
            <p className="text-sm text-[#8B4513] mt-2 font-medium">{currentTierInfo.range}</p>
          )}
        </div>

        {/* Tier Filter Tabs */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center bg-white rounded-full px-1 py-1 border border-gray-100">
            {priceTiers.map((tier) => (
              <button
                key={tier.id}
                onClick={() => onTierChange?.(tier.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTier === tier.id
                    ? 'bg-[#1a1a1a] text-white'
                    : 'text-gray-500 hover:text-[#1a1a1a]'
                }`}
              >
                {tier.label}
              </button>
            ))}
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex items-center justify-center mb-10">
          <div className="flex items-center bg-white rounded-full px-1 py-1 border border-gray-100">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.id
                    ? 'bg-[#8B4513] text-white'
                    : 'text-gray-500 hover:text-[#1a1a1a]'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Product Grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
          data-product-shop
        >
          {filtered.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20 text-gray-400">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-inbox-line text-5xl"></i>
            </span>
            <p className="text-base">此分類暫無商品</p>
          </div>
        )}
      </div>
    </section>
  );
}
